import moment from 'moment';
import React from 'react';

import { useEvent } from '@legacy-providers/event';
import { getParkings } from '@legacy-services/api/parkings';

import { Data } from '@shared/api/types';
import { EditParkingRequestButton } from '@shared/parking-requests/components/EditParkingRequestButton';
import { deleteParkingRequest } from '@shared/parking-requests/endpoints/deleteParkingRequest';
import { getRegistrationParkingOptions } from '@shared/parking-requests/endpoints/getRegistrationParkingOptions';
import { getEventParkingOptions } from '@shared/parkings/endpoints/getEventParkingOptions';
import { DataTable } from '@shared/ui/components/DataTable';
import { Icon } from '@shared/ui/components/Icon';

type ParkingRequestParkingSticker = App.Domain.Parking.Data.ParkingRequestParkingStickerData;
type ParkingRequest = App.Domain.Parking.Data.ParkingRequestData;
type Columns = 'crewMember' | 'parking' | 'quantity' | 'days';

type ParkingRequestsDataTableProps = {
  parkingRequests: Data<ParkingRequest>;
  registrationId?: number;
  columns?: Partial<{ [k in Columns]: boolean }>;
  avoidPagination?: boolean;
  withoutFilters?: boolean;
};

export function ParkingRequestsDataTable({
  parkingRequests,
  registrationId,
  columns = {},
  avoidPagination = false,
  withoutFilters = false,
}: ParkingRequestsDataTableProps) {
  const event = useEvent();

  return (
    <DataTable<ParkingRequest>
      data={parkingRequests}
      columns={[
        {
          visible: columns?.crewMember !== false,
          name: 'crewMember.user.fullName',
          label: 'Crew Member',
          filterable: {
            name: 'user_name',
            label: 'Crew Member',
            icon: 'user',
          },
          sortable: false,
          render: (row) => row.crewMember!.user.fullName,
        },
        {
          visible: columns?.parking !== false,
          name: 'parking.name',
          label: 'Parking',
          filterable: {
            name: 'parking_id',
            label: 'Parking',
            icon: 'car',
            type: 'select',
            options: (query) =>
              registrationId
                ? getRegistrationParkingOptions(registrationId, query)
                : getEventParkingOptions(event.id, query),
          },
          sortable: false,
          render: (row) => row.parking.name,
        },
        {
          visible: columns?.quantity !== false,
          name: 'quantity',
          label: 'Quantity',
        },
        {
          visible: columns?.days !== false,
          name: 'days',
          label: 'Days',
          render: (row) => row.days.map((day) => moment(day).format('dd DD/MM')).join(', '),
        },
        {
          label: 'Assigned Stickers',
          render: (row) => {
            const groupedStickers = row.parkingRequestParkingStickers!.reduce<{
              [key: string]: ParkingRequestParkingSticker & { count: number };
            }>((groupedStickers, sticker) => {
              const key = `${sticker.parkingStickerId}-${sticker.claimedAt ? 'claimed' : 'unclaimed'}`;
              if (!groupedStickers[key]) {
                groupedStickers[key] = {
                  ...sticker,
                  count: 1,
                };
              } else {
                groupedStickers[key].count += 1;
              }
              return groupedStickers;
            }, {});

            return (
              <div className="text-sm grid grid-cols-[max-content_auto] gap-x-2">
                {Object.values(groupedStickers).map((sticker) => (
                  <div key={`${sticker.parkingStickerId}-${sticker.claimedAt}`} className="contents">
                    <span>{`${sticker.count} x ${sticker.parkingSticker!.name}`}</span>
                    <span className="font-medium min-w-max">
                      {sticker.claimedAt && (
                        <>
                          <Icon icon="hand-out" /> Claimed
                        </>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            );
          },
        },
        {
          label: 'Updated',
          name: 'updatedAt',
          type: 'datetime',
        },
        {
          render: (row) => row.hasUnclaimedStickers && <EditParkingRequestButton parkingRequest={row} />,
          type: 'actions',
        },
      ]}
      onDelete={deleteParkingRequest}
      avoidPagination={avoidPagination}
      withoutFilters={withoutFilters}
    />
  );
}
